<template>
  <div>
    <b-row>
      <!-- Source Card -->
      <b-col lg="6" class="mb-2">
        <b-card class="resources-list-card" no-body>
          <div class="gradient-line-wrapper"></div>
          <div class="p-1">
            <div class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2">
              <div>
                <h5 class="mb-0">Source</h5>
              </div>
            </div>
            
            <b-row class="align-items-center filter-row-single-line mb-2">
              <b-col xl="6" lg="6" md="6" sm="12" cols="12">
                <b-form-group label="Campus" class="mb-0 w-100" invalid-feedback="Please select a campus">
                  <v-select
                    v-model="sourceFilter.campus"
                    :options="campuses"
                    :reduce="(val) => val.id"
                    label="branch"
                    :clearable="false"
                    @input="LoadSourceDepartments"
                    placeholder="Select campus"
                    class="w-100"
                  />
                </b-form-group>
              </b-col>
              <b-col xl="6" lg="6" md="6" sm="12" cols="12">
                <b-form-group label="Department" class="mb-0 w-100" invalid-feedback="Please select a department">
                  <v-select
                    v-model="sourceFilter.department"
                    :options="sourceDepartments"
                    :reduce="(val) => val.id"
                    label="name"
                    :clearable="true"
                    placeholder="Select department"
                    :disabled="loadingSourceDeps || !sourceFilter.campus"
                    class="w-100"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col cols="12" md="3" sm="4">
                <b-button
                  variant="primary"
                  block
                  :disabled="loading || destLoading || transferring"
                  @click="validateSource"
                >
                  <b-spinner v-if="loading" small />
                  <span v-else>Show</span>
                </b-button>
              </b-col>
            </b-row>
            <b-row class="mt-1 align-items-center">
              <b-col xl="6" lg="6" md="6" class="mb-1">
                <b-form-input v-model.trim="searchQuery" placeholder="Search..." class="filter-control" />
              </b-col>
              <b-col xl="6" lg="6" md="6" class="d-flex flex-wrap align-items-center justify-content-end mb-1">
                <h5 class="mb-0 mr-2">Total {{ sourceStaff.length }} Staff</h5>
                <b-button
                  v-if="sourceStaff.length > 0 && rights.edit"
                  variant="success"
                  class="action-btn-filter"
                  :disabled="!isValidSource || transferring"
                  v-b-tooltip.hover.top="'This action will transfer the selected staff to the destination department.'"
                  @click="confirmTransfer"
                >
                  <b-spinner v-if="transferring" small class="mr-1" />
                  <span v-else>
                    <feather-icon icon="RepeatIcon" size="16" class="mr-1" />
                    <span class="align-middle">Transfer Staff</span>
                    <span v-if="selectedIDs.length > 0" class="ml-1">({{ selectedIDs.length }})</span>
                  </span>
                </b-button>
              </b-col>
            </b-row>

            <div class="table-wrapper">
              <b-table
                :items="filteredStaff"
                :fields="fields"
                responsive
                hover
                show-empty
                :busy="loading"
                small
                class="resources-table mb-0 mt-1"
                @row-clicked="onRowSelect"
              >
                <template #table-busy>
                  <tbody>
                    <tr v-for="n in 5" :key="'src-skeleton-' + n" class="skeleton-row">
                      <td v-for="(field, index) in fields" :key="'src-skeleton-cell-' + index" class="skeleton-cell">
                        <div class="skeleton-loader"></div>
                      </td>
                    </tr>
                  </tbody>
                </template>

                <template #head(index)>
                  <div>
                    <span class="font-small-2">SELECT</span>
                  </div>
                </template>

                <template #cell(index)="data">
                  <b-form-radio
                    v-model="singleSelectedID"
                    :value="data.item.id"
                    :disabled="transferring"
                    name="staff-transfer-selection"
                    @change="onRowSelect(data.item)"
                  >
                    <b-badge variant="light-primary">{{ data.index + 1 }}</b-badge>
                  </b-form-radio>
                </template>

                <template #cell(name)="data">
                  <b-media vertical-align="center">
                    <template #aside>
                      <b-avatar size="44" :src="data.item.picture" variant="light-primary" />
                    </template>
                    <b-link class="font-weight-bold d-block text-nowrap mb-25">
                      {{ data.item.name }}
                    </b-link>
                    <small class="text-muted">
                      {{ data.item.designation || 'Staff' }}
                    </small>
                  </b-media>
                </template>

                <template #cell(department)="data">
                  <b-badge variant="light-primary">{{ getSourceDepartmentName(data.item.departmentID) }}</b-badge>
                </template>
              </b-table>
            </div>
          </div>
        </b-card>
      </b-col>

      <!-- Destination Card -->
      <b-col lg="6">
        <b-card class="resources-list-card" no-body>
          <div class="gradient-line-wrapper"></div>
          <div class="p-1">
            <div class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2">
              <div>
                <h5 class="mb-0">Destination</h5>
              </div>
            </div>
            
            <b-row class="align-items-center filter-row-single-line mb-2">
              <b-col xl="6" lg="6" md="6" sm="12" cols="12">
                <b-form-group label="Campus" class="mb-0 w-100" invalid-feedback="Please select a campus">
                  <v-select
                    v-model="destFilter.campus"
                    :options="filteredDestCampuses"
                    :reduce="(val) => val.id"
                    label="branch"
                    :clearable="false"
                    @input="LoadDestDepartments"
                    placeholder="Select campus"
                    class="w-100"
                  />
                </b-form-group>
              </b-col>
              <b-col xl="6" lg="6" md="6" sm="12" cols="12">
                <b-form-group label="Department" class="mb-0 w-100" invalid-feedback="Please select a department">
                  <v-select
                    v-model="destFilter.department"
                    :options="destDepartments"
                    :reduce="(val) => val.id"
                    label="name"
                    :clearable="false"
                    placeholder="Select department"
                    :disabled="loadingDestDeps || !destFilter.campus"
                    @input="validateDestination"
                    class="w-100"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row class="mt-1 mb-2">
              <b-col md="12">
                <h5 class="mb-0">Total {{ destStaff.length }} Staff</h5>
              </b-col>
            </b-row>

            <div class="table-wrapper mt-3">
              <b-table
                :items="destStaff"
                :fields="destFields"
                responsive
                hover
                show-empty
                :busy="destLoading"
                small
                class="resources-table mb-0 mt-1"
              >
                <template #table-busy>
                  <tbody>
                    <tr v-for="n in 5" :key="'dest-skeleton-' + n" class="skeleton-row">
                      <td v-for="(field, index) in fields" :key="'dest-skeleton-cell-' + index" class="skeleton-cell">
                        <div class="skeleton-loader"></div>
                      </td>
                    </tr>
                  </tbody>
                </template>

                <template #cell(index)="data">
                  {{ data.index + 1 }}
                </template>

                <template #cell(name)="data">
                  <b-media vertical-align="center">
                    <template #aside>
                      <b-avatar size="44" :src="data.item.picture" variant="light-primary" />
                    </template>
                    <b-link class="font-weight-bold d-block text-nowrap mb-25">
                      {{ data.item.name }}
                    </b-link>
                    <small class="text-muted">
                      {{ data.item.designation || 'Staff' }}
                    </small>
                  </b-media>
                </template>

                <template #cell(department)="data">
                  <b-badge variant="light-primary">{{ getDestDepartmentName(data.item.departmentID) }}</b-badge>
                </template>
              </b-table>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Response Modal -->
    <b-modal
      id="response-modal"
      title="Transfer Details"
      ok-only
      ok-variant="primary"
      size="lg"
      centered
      no-close-on-backdrop
    >
      <div v-for="(resp, i) in transferResponses" :key="'resp-' + i" class="mb-3">
        <h5 class="mb-2">
          <b-badge variant="light-primary" class="mr-1">Staff</b-badge>
          {{ resp.staffName }}
        </h5>
        
        <app-timeline class="mt-1 mb-0">
          <app-timeline-item
            v-for="(desc, idx) in resp.description"
            :key="'desc-' + idx"
            :variant="getTimelineVariant(idx, resp.description.length)"
          >
            <p class="mb-0" v-html="formatMarkdown(desc)"></p>
          </app-timeline-item>
        </app-timeline>

        <hr v-if="i < transferResponses.length - 1" class="my-2" />
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow, BCol, BFormGroup, BFormInput, BCard, BButton,
  BMedia, BSpinner, BBadge, BFormRadio, BTable, BAvatar,
  BModal, VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions } from "vuex";
import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'

export default {
  components: {
    BRow, BCol, BFormGroup, BFormInput, BCard, BButton,
    BMedia, BSpinner, BBadge, BFormRadio, BTable, BAvatar, vSelect,
    BModal, AppTimeline, AppTimelineItem,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      rights: {},
      searchQuery: "",
      loading: false,
      destLoading: false,
      transferring: false,
      transferResponses: [],
      loadingSourceDeps: false,
      loadingDestDeps: false,
      campuses: [],
      sourceDepartments: [],
      destDepartments: [],
      sourceStaff: [],
      destStaff: [],
      sourceFilter: {
        campus: 0,
        department: null,
      },
      destFilter: {
        campus: 0,
        department: null,
      },
      fields: [
        { key: "index", label: "S.No" },
        { key: "name", label: "Name" },
        { key: "department", label: "Department" },
      ],
      destFields: [
        { key: "index", label: "S.No" },
        { key: "name", label: "Name" },
        { key: "department", label: "Department" },
      ],
      isValidSource: false,
      isValidDestination: false,
      selectedIDs: [],
      sourceSelectionAtLoad: {
        campus: 0,
        department: null,
      },
    };
  },
  created() {
    const routeRights = this.$store.state.rights[this.$route.name];
    if (!routeRights) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = routeRights;
    this.LoadCampuses();
  },
  computed: {
    filteredStaff() {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) return this.sourceStaff;

      return this.sourceStaff.filter((staff) => {
        const name = (staff.name || "").toLowerCase();
        const designation = (staff.designation || "").toLowerCase();
        return name.includes(query) || designation.includes(query);
      });
    },
    filteredDestCampuses() {
      if (!this.sourceFilter.campus) return this.campuses;
      return this.campuses.filter(c => c.id !== this.sourceFilter.campus);
    },
    singleSelectedID: {
      get() {
        return this.selectedIDs.length > 0 ? this.selectedIDs[0] : null;
      },
      set(val) {
        this.selectedIDs = val ? [val] : [];
      }
    },
  },
  methods: {
    ...mapActions({ get: "get", post: "post" }),

    onRowSelect(ch) {
      if (ch && !this.transferring) {
        this.selectedIDs = [ch.id];
      }
    },

    getSourceDepartmentName(id) {
      const dep = this.sourceDepartments.find(d => d.id === id);
      return dep ? dep.name : "N/A";
    },

    getDestDepartmentName(id) {
      const dep = this.destDepartments.find(d => d.id === id);
      return dep ? dep.name : "N/A";
    },

    async LoadCampuses() {
      const obj = {
        url:
          this.$store.state.domain +
          "campuses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.campuses = await this.get(obj);
    },

    async LoadSourceDepartments() {
      this.sourceFilter.department = null;
      this.sourceDepartments = [];
      this.loadingSourceDeps = true;

      if (!this.sourceFilter.campus) {
        this.loadingSourceDeps = false;
        return;
      }

      const obj = {
        url:
          this.$store.state.domain +
          "StaffDepartments?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.sourceFilter.campus,
        token: this.$store.state.userData.token,
      };
      this.sourceDepartments = await this.get(obj);
      this.loadingSourceDeps = false;
    },

    async LoadDestDepartments() {
      this.destFilter.department = null;
      this.destDepartments = [];
      this.loadingDestDeps = true;

      if (!this.destFilter.campus) {
        this.loadingDestDeps = false;
        return;
      }

      const obj = {
        url:
          this.$store.state.domain +
          "StaffDepartments?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.destFilter.campus,
        token: this.$store.state.userData.token,
      };
      this.destDepartments = await this.get(obj);
      this.loadingDestDeps = false;
    },

    validateSource() {
      this.isValidSource = this.sourceFilter.campus;
      if (this.isValidSource) {
        this.LoadSourceStaff();
      } else {
        this.sourceStaff = [];
      }
    },

    validateDestination() {
      this.isValidDestination =
        this.destFilter.campus && this.destFilter.department;
      if (this.isValidDestination) {
        this.LoadDestStaff();
      } else {
        this.destStaff = [];
      }
    },

    async LoadSourceStaff() {
      this.selectedIDs = [];
      this.loading = true;
      try {
        this.sourceSelectionAtLoad = {
          campus: this.sourceFilter.campus,
          department: this.sourceFilter.department || 0,
        };

        const depId = this.sourceFilter.department ? this.sourceFilter.department : 0;
        
        let url = this.$store.state.domain +
          "staff/LoadDataPagination?db=" +
          this.$store.state.userData.db +
          "&cId=" + this.sourceFilter.campus +
          "&dptID=" + depId +
          "&status=present" +
          "&page=1" +
          "&pageSize=1000";

        const obj = {
          url: url,
          token: this.$store.state.userData.token,
          dashboard: true,
        };
        const res = await this.get(obj);
        this.sourceStaff = res && res.data ? res.data : [];
      } catch (error) {
        console.error("Error loading source staff:", error);
        this.sourceStaff = [];
      }
      this.loading = false;
    },

    async LoadDestStaff() {
      this.destLoading = true;
      try {
        let url = this.$store.state.domain +
          "staff/LoadDataPagination?db=" +
          this.$store.state.userData.db +
          "&cId=" + this.destFilter.campus +
          "&dptID=" + this.destFilter.department +
          "&status=present" +
          "&page=1" +
          "&pageSize=1000";

        const obj = {
          url: url,
          token: this.$store.state.userData.token,
          dashboard: true,
        };
        const res = await this.get(obj);
        this.destStaff = res && res.data ? res.data : [];
      } catch (error) {
        console.error("Error loading destination staff:", error);
        this.destStaff = [];
      }
      this.destLoading = false;
    },

    async confirmTransfer() {
      if (this.selectedIDs.length == 0) {
        this.$bvToast.toast("Please select the staff to transfer", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }

      this.isValidDestination = this.destFilter.campus;

      if (!this.isValidDestination) {
        this.$bvToast.toast(
          "Please select destination campus",
          {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          }
        );
        return;
      }

      if (this.sourceSelectionAtLoad.campus === this.destFilter.campus) {
        this.$bvToast.toast(
          "Source and destination campuses cannot be the exact same",
          {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          }
        );
        return;
      }

      const result = await Swal.fire({
        title: this.$t("Transfer Staff"),
        text: "Please provide transfer notes for " + this.selectedIDs.length + " staff members",
        input: 'textarea',
        inputPlaceholder: 'Enter notes here...',
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, transfer!",
        cancelButtonText: "No, cancel!",
        customClass: {
          confirmButton: "btn btn-success",
          cancelButton: "btn btn-danger ml-1",
        },
        buttonsStyling: false,
      });

      if (result.isConfirmed) {
        await this.transferStaff(result.value);
      }
    },

    async transferStaff(notes) {
      this.transferring = true;
      let successCount = 0;
      let failCount = 0;
      
      this.transferResponses = [];

      for (const tID of this.selectedIDs) {
        const staffObj = this.sourceStaff.find(s => s.id === tID);
        if (!staffObj) continue;

        try {
          const url = this.$store.state.domain +
            "Staff/TransferStaff?db=" +
            this.$store.state.userData.db +
            "&staffID=" + tID +
            "&toCampusID=" + this.destFilter.campus +
            "&toDepartmentID=" + this.destFilter.department +
            "&transferNotes=" + encodeURIComponent(notes || "") +
            "&userID=" + this.$store.state.userData.userID;

          const obj = {
            url: url,
            token: this.$store.state.userData.token,
            context: this,
            allResponse: true,
          };
          
          const res = await this.post(obj);

          if (!res) throw new Error("Staff transfer request did not complete successfully or returned an internal framework error.");

          const responseData = res && res.data ? res.data : res;
          let descriptionArray = [];
          if (responseData && Array.isArray(responseData.description)) {
            descriptionArray = responseData.description;
          } else if (res && Array.isArray(res.description)) {
            descriptionArray = res.description;
          } else if (responseData && responseData.message) {
            descriptionArray = [responseData.message];
          } else if (res && res.message) {
            descriptionArray = [res.message];
          } else if (responseData && typeof responseData.description === 'string') {
            descriptionArray = [responseData.description];
          }

          if (descriptionArray.length > 0) {
            this.transferResponses.push({
              staffName: staffObj.name,
              description: descriptionArray.filter(d => {
                if (typeof d !== 'string' || d.trim() === '') return false;
                const lower = d.toLowerCase();
                // Hide specific elements as requested
                if (lower.includes('login credentials associated with')) return false;
                if (lower.includes('transfer history and an audit log')) return false;
                return true;
              })
            });
          } else {
            this.transferResponses.push({
              staffName: staffObj.name,
              description: ["Staff Transferred Successfully."]
            });
          }

          successCount++;
        } catch (error) {
          console.error("Error transferring staff (tID: " + tID + "):", error);
          let errDescArray = [];
          if (error.response && error.response.data && Array.isArray(error.response.data.description)) {
            errDescArray = error.response.data.description;
          } else if (error.data && Array.isArray(error.data.description)) {
            errDescArray = error.data.description;
          }

          if (errDescArray.length > 0) {
            this.transferResponses.push({
              staffName: staffObj.name,
              description: errDescArray.filter(d => {
                if (typeof d !== 'string' || d.trim() === '') return false;
                const lower = d.toLowerCase();
                if (lower.includes('login credentials associated with')) return false;
                if (lower.includes('transfer history and an audit log')) return false;
                return true;
              })
            });
          } else {
            this.transferResponses.push({
              staffName: staffObj.name,
              description: [error.message || "Transfer failed. Please check the logs or try again."]
            });
          }
          failCount++;
        }
      }

      if (successCount > 0) {
        this.$bvToast.toast(`${successCount} staff transferred successfully!`, {
          title: "Success",
          variant: "success",
          toaster: "b-toaster-top-center",
        });
        await this.LoadSourceStaff();
        await this.LoadDestStaff();
      }
      if (failCount > 0) {
        this.$bvToast.toast(`Failed to transfer ${failCount} staff`, {
          title: "Error",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      }

      this.transferring = false;
      this.selectedIDs = [];

      if (this.transferResponses.length > 0) {
        this.$bvModal.show('response-modal');
      }
    },
    
    formatMarkdown(text) {
      if (!text) return "";
      return text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
    },

    getTimelineVariant(idx, total) {
      if (idx === 0) return "success";
      if (idx === total - 1) return "info";
      return "primary";
    },

  },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";

// Resources List Card
.resources-list-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem 1.75rem 1.5rem 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 1.5rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: visible; // Changed from hidden to visible to allow dropdowns to show properly
  animation: slideUpFade 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  z-index: 1;

  // Gradient Line Wrapper - Aligned with content, shorter length
  .gradient-line-wrapper {
    position: absolute;
    top: 0;
    left: 0.75rem; // Reduced margin to touch curve
    right: 0.75rem; // Reduced margin to touch curve
    width: calc(100% - 1.5rem); // Subtract left and right padding
    height: 5px;
    overflow: hidden;
    border-radius: 10px 10px 0 0;
    z-index: 1;
    pointer-events: none;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 5px;
      background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
      border-radius: 10px 10px 0 0;
    }
  }

  &:hover {
    box-shadow: 0 15px 40px rgba(115, 103, 240, 0.2);
    transform: translateY(-2px);
  }

  // Filter row styling inside card
  .filter-row-single-line {
    background: transparent !important;
    padding: 0;
    margin-bottom: 1rem;
    gap: 0.5rem;
    flex-wrap: wrap;
    align-items: center;

    @media (min-width: 1200px) {
      flex-wrap: nowrap;
    }

    > [class*="col-"] {
      padding-left: 0.375rem;
      padding-right: 0.375rem;
      display: flex;
      align-items: center;
      background: transparent !important;
    }
  }

}

.table-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0edff;
  margin-bottom: 1rem;

  h5 {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
  }

  small {
    font-size: 0.85rem;
    color: #6b7280;
    font-weight: 500;
  }
}

// Filter Control - Consistent height and styling for all filters
.filter-control {
  height: 42px !important;
  min-height: 42px !important;
  border-radius: 10px;
  border: 1.5px solid #e0d8ff;
  padding: 0.5rem 0.75rem;
  background: #fff;
  transition: all 0.2s ease;
  color: #2d1b69;
  font-weight: 500;
  width: 100%;

  &:hover {
    border-color: #b8a9ff;
  }

  &:focus {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
    outline: none;
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }
}

// Action Buttons
.action-btn-filter {
  height: 42px;
  font-weight: 600;
  border-radius: 10px;
  padding: 0.5rem 1rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.table-wrapper {
  position: relative;
  overflow-x: auto;
  overflow-y: visible;
  width: 100%;
  -webkit-overflow-scrolling: touch;

  // Bottom scrollbar styling
  &::-webkit-scrollbar {
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;

    &:hover {
      background: #94a3b8;
    }
  }

  // Firefox scrollbar
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}

// Resources Table - Modern Compact Design
.resources-table {
  margin-top: 1rem;
  border-radius: 12px;
  overflow: hidden;
  min-width: 100%;
  width: max-content;
  border: 1px solid #e5e7eb;

  ::v-deep th {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: none;
    font-weight: 700;
    color: #1e293b;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    padding: 0.75rem 0.875rem;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 5;

    &::after {
      display: none;
    }
  }

  ::v-deep td {
    vertical-align: middle;
    border-top: 1px solid #f1f5f9;
    border-bottom: 1px solid #f8fafc;
    padding: 0.75rem 0.875rem;
    background: #ffffff;
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    font-size: 0.875rem;
    color: #475569;
    white-space: nowrap;

    &.title-cell {
      vertical-align: top !important;
      white-space: normal !important;
      word-break: break-word !important;
      word-wrap: break-word !important;
      max-width: 500px !important;
      min-width: 250px !important;
      width: auto !important;
      padding: 0.75rem 1rem !important;
      line-height: 1.6 !important;
    }
  }

  ::v-deep tbody tr {
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    position: relative;
    border-left: 2px solid transparent;

    &:nth-child(even) {
      background: #f8fafc;

      td {
        background: #f8fafc;
      }
    }

    &:nth-child(odd) {
      background: #ffffff;

      td {
        background: #ffffff;
      }
    }

    &:hover {
      background: #f1f5f9 !important;
      border-left-color: #7c5dfa;
      box-shadow: -2px 0 8px rgba(124, 93, 250, 0.1);
      z-index: 1;
      position: relative;

      td {
        background: transparent !important;
        color: #1e293b;
        font-weight: 500;
      }
    }

    &:active {
      transform: none;
    }
  }

  ::v-deep .badge {
    font-size: 0.7rem;
    padding: 0.25rem 0.5rem;
    border-radius: 6px;
    font-weight: 600;
    line-height: 1.2;
  }

  // Compact cell content
  ::v-deep .text-muted {
    font-size: 0.8rem;
    color: #64748b;
  }
}

// Skeleton Loader Styles - Premium Animated Effect
.skeleton-row {
  animation: skeletonPulse 2s ease-in-out infinite;
  transition: opacity 0.3s ease;

  &:nth-child(1) {
    animation-delay: 0s;
  }

  &:nth-child(2) {
    animation-delay: 0.15s;
  }

  &:nth-child(3) {
    animation-delay: 0.3s;
  }

  &:nth-child(4) {
    animation-delay: 0.45s;
  }

  &:nth-child(5) {
    animation-delay: 0.6s;
  }

  &:hover {
    background: transparent !important;
    border-left-color: transparent !important;
    box-shadow: none !important;
    transform: none !important;

    td {
      background: transparent !important;
      color: inherit !important;
      font-weight: normal !important;
    }
  }
}

.skeleton-cell {
  padding: 0.875rem 0.875rem !important;
  vertical-align: middle !important;
  border-top: 1px solid #f1f5f9 !important;
  border-bottom: 1px solid #f8fafc !important;
  background: #ffffff !important;
  position: relative;

  &:nth-child(even) {
    background: #fafbfc !important;
  }

  // Add subtle border animation
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 4px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(124, 93, 250, 0.03) 50%,
      transparent 100%
    );
    animation: skeletonBorderGlow 2.5s ease-in-out infinite;
    pointer-events: none;
  }
}

.skeleton-loader {
  height: 18px;
  border-radius: 10px;
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #f8f8f8 25%,
    #f0f0f0 50%,
    #f8f8f8 75%,
    #f0f0f0 100%
  );
  background-size: 300% 100%;
  animation: skeletonShimmer 2s ease-in-out infinite;
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  // Premium shimmer effect
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.6) 50%,
      transparent 100%
    );
    animation: skeletonShine 2s ease-in-out infinite;
    filter: blur(8px);
  }

  // Secondary glow layer
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(124, 93, 250, 0.08) 30%,
      rgba(157, 122, 255, 0.12) 50%,
      rgba(124, 93, 250, 0.08) 70%,
      transparent 100%
    );
    animation: skeletonColorWave 2.5s ease-in-out infinite;
    border-radius: 10px;
  }
}

// Different widths for different column positions
.skeleton-cell {
  &:nth-child(1) .skeleton-loader {
    width: 50px;
    margin: 0 auto;
  }

  &:nth-child(2) .skeleton-loader {
    width: 70px;
  }

  &:nth-child(3) .skeleton-loader {
    width: 140px;
  }

  &:nth-child(4) .skeleton-loader {
    width: 110px;
  }

  &:nth-child(5) .skeleton-loader {
    width: 100px;
  }
}

@keyframes skeletonShimmer {
  0% {
    background-position: -300% 0;
  }
  100% {
    background-position: 300% 0;
  }
}

@keyframes skeletonShine {
  0% {
    left: -100%;
  }
  50% {
    left: 100%;
  }
  100% {
    left: 100%;
  }
}

@keyframes skeletonColorWave {
  0%,
  100% {
    opacity: 0.3;
    transform: scaleX(0.8);
  }
  50% {
    opacity: 0.6;
    transform: scaleX(1);
  }
}

@keyframes skeletonBorderGlow {
  0%,
  100% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
}

@keyframes skeletonPulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.85;
    transform: scale(0.998);
  }
}

@keyframes slideUpFade {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
